import type { TenantAuditEventSummary } from '@nadoutong/contracts';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlatformApiError } from '../../api/client.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useTenantAuditEvents } from '../../api/useOrgs.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  Input,
  Pagination,
  Panel,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';

/**
 * Tenant audit log — the `audit` tab of `/organization` (ported from
 * console-web's routed `/team/audit` page, epic #1 PR-4).
 *
 * Filters live in URL search params (`action`, `target`), debounced 300ms
 * and written with `replace` so typing does not spam history — the source
 * page's semantics, unchanged. The log being a tab rather than a route is
 * no obstacle: `/organization` now carries its tab selection in `?tab=`
 * too, so `?tab=audit&action=…` restores the exact filtered view.
 *
 * One deliberate departure from the source page:
 *
 *  - The action cell is a `normal-case` `Chip`: an audit action is an
 *    identifier (`member_model_permissions.updated`), and the chip's
 *    default `uppercase` would misrepresent it.
 *
 * Pagination derives from the server `limit` — a full page implies a next
 * page, because the endpoint reports no total.
 *
 * Permission gating lives in the parent: the tab is not offered at all
 * without `org:audit:read` / `member:audit:read`, so this panel never
 * mounts (and never queries) for a member who may not read the log.
 */

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;

interface Props {
  readonly orgId: string | undefined;
}

export function AuditPanel({ orgId }: Props): JSX.Element {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get('action') ?? '';
  const target = searchParams.get('target') ?? '';

  const [actionInput, setActionInput] = useState(action);
  const [targetInput, setTargetInput] = useState(target);
  const [offset, setOffset] = useState(0);

  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
    setOffset(0);
  }

  // Keep inputs in sync when the URL changes from outside (deep link,
  // clear button); the debounce below only fires when values differ.
  useEffect(() => {
    setActionInput(action);
  }, [action]);
  useEffect(() => {
    setTargetInput(target);
  }, [target]);

  // Debounce the two text fields into the applied filter pair; typing
  // must not fire a request per keystroke. Any applied change resets to
  // the first page.
  useEffect(() => {
    const nextAction = actionInput.trim();
    const nextTarget = targetInput.trim();
    if (nextAction === action && nextTarget === target) {
      return undefined;
    }
    const timer = setTimeout(() => {
      patchParams({ action: nextAction, target: nextTarget });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [actionInput, targetInput, action, target]);

  function clearFilters(): void {
    patchParams({ action: null, target: null });
  }

  const auditQ = useTenantAuditEvents(orgId, {
    limit: PAGE_SIZE,
    offset,
    action: action.length > 0 ? action : undefined,
    targetAccountId: target.length > 0 ? target : undefined,
  });

  const events = auditQ.data?.events ?? [];
  const limit = auditQ.data?.limit ?? PAGE_SIZE;
  const page = Math.floor(offset / Math.max(1, limit)) + 1;
  const hasNext = events.length >= limit;
  const pageCount = hasNext ? page + 1 : page;
  const hasFilter = action.length > 0 || target.length > 0;

  return (
    <div data-testid="organization-audit" className="flex flex-col gap-4">
      <Panel
        caption={t('organization.audit.filter.caption')}
        padded
        testId="organization-audit-filter-bar"
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-caption text-ink-low">{t('organization.audit.subtitle')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              mono
              value={actionInput}
              onChange={(event) => setActionInput(event.currentTarget.value)}
              placeholder={t('organization.audit.filter.action_placeholder')}
              aria-label={t('organization.audit.filter.action_label')}
              className="w-96 max-w-full"
              data-testid="organization-audit-action-filter"
            />
            <Input
              mono
              value={targetInput}
              onChange={(event) => setTargetInput(event.currentTarget.value)}
              placeholder={t('organization.audit.filter.target_placeholder')}
              aria-label={t('organization.audit.filter.target_label')}
              className="w-64 max-w-full"
              data-testid="organization-audit-target-filter"
            />
            {hasFilter ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="organization-audit-clear-filters"
              >
                {t('organization.audit.filter.clear')}
              </Button>
            ) : null}
          </div>
        </div>
      </Panel>

      <Panel caption={t('organization.audit.title')} testId="organization-audit-events">
        {auditQ.isLoading ? (
          <TableSkeleton rows={5} testId="organization-audit-loading" />
        ) : auditQ.isError ? (
          <div className="p-4">
            <PanelError
              message={formatAuditError(auditQ.error, t('organization.audit.error.load'), t)}
              retryLabel={t('organization.audit.retry')}
              onRetry={() => {
                void auditQ.refetch();
              }}
              testId="organization-audit-error"
            />
          </div>
        ) : events.length === 0 ? (
          <EmptyState
            testId="organization-audit-empty"
            title={t('organization.audit.empty')}
            hint={t('organization.audit.empty_hint')}
          />
        ) : (
          <Table className="rounded-none border-0" testId="organization-audit-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('organization.audit.column.time')}</Th>
                <Th>{t('organization.audit.column.actor')}</Th>
                <Th>{t('organization.audit.column.action')}</Th>
                <Th>{t('organization.audit.column.target')}</Th>
                <Th>{t('organization.audit.column.detail')}</Th>
              </Tr>
            </THead>
            <TBody>
              {events.map((event) => (
                <Tr key={event.id} data-testid={`organization-audit-row-${event.id}`}>
                  <Td mono heading>
                    {formatTimestamp(event.createdAt)}
                  </Td>
                  <Td mono label={t('organization.audit.column.actor')}>
                    {event.actorAccountId ?? t('organization.audit.actor_system')}
                  </Td>
                  <Td label={t('organization.audit.column.action')}>
                    <Chip
                      tone="neutral"
                      lamp={false}
                      className="normal-case"
                      testId={`organization-audit-action-${event.id}`}
                    >
                      {event.action}
                    </Chip>
                  </Td>
                  <Td mono label={t('organization.audit.column.target')}>
                    {event.targetAccountId ?? '--'}
                  </Td>
                  <Td label={t('organization.audit.column.detail')} stack>
                    <span className="break-words font-mono text-caption text-ink-low">
                      {formatAuditMetadata(event.metadata)}
                    </span>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
        <div className="flex items-center justify-end border-t border-line-soft px-4 py-3">
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) => setOffset((next - 1) * limit)}
            prevLabel={t('organization.audit.pagination.previous')}
            nextLabel={t('organization.audit.pagination.next')}
            ariaLabel={t('organization.audit.pagination.aria')}
            testId="organization-audit-pagination"
          />
        </div>
      </Panel>
    </div>
  );
}

function formatAuditError(error: unknown, fallback: string, t: ReturnType<typeof useT>): string {
  return error instanceof PlatformApiError ? localizeApiError(error, t) : fallback;
}

function formatAuditMetadata(metadata: TenantAuditEventSummary['metadata']): string {
  const entries = Object.entries(metadata);
  if (entries.length === 0) return '--';
  return entries
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${formatAuditValue(value)}`)
    .join(' · ');
}

function formatAuditValue(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${String(value.length)}]`;
  }
  if (typeof value === 'object') {
    return '{...}';
  }
  return String(value);
}
